import { useState, useEffect } from 'react';
import { Link } from 'wouter';
import { fetchBlogPosts, fetchBlogTags, formatDate, type BlogPost, type BlogTag } from '@/lib/blog-api';
import { Clock, Tag, BookOpen } from 'lucide-react';
import { SEO } from "@/components/seo";

function getSSRData(): { posts: BlogPost[]; tags: BlogTag[] } | null {
  const data = (window as any).__BLOG_DATA__;
  if (data?.type === 'listing') {
    return { posts: data.posts, tags: data.tags };
  }
  return null;
}

export default function BlogPage() {
  const ssrData = getSSRData();
  const [posts, setPosts] = useState<BlogPost[]>(ssrData?.posts ?? []);
  const [tags, setTags] = useState<BlogTag[]>(ssrData?.tags ?? []);
  const [loading, setLoading] = useState(!ssrData);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Clean up SSR data to avoid stale state on SPA navigation
    if ((window as any).__BLOG_DATA__?.type === 'listing') {
      delete (window as any).__BLOG_DATA__;
      return;
    }

    async function loadData() {
      try {
        setLoading(true);
        const [postsData, tagsData] = await Promise.all([
          fetchBlogPosts(50),
          fetchBlogTags(),
        ]);
        setPosts(postsData.posts);
        setTags(tagsData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load blog');
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white">
        <div className="container mx-auto px-4 py-16">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-pulse text-white/60">Loading blog...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white">
        <div className="container mx-auto px-4 py-16">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Oops!</h1>
            <p className="text-white/60 mb-6">{error}</p>
            <Link href="/" className="text-blue-400 hover:underline">
              Go back home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <SEO
        title="Blog | BounceBunny"
        description="Tips, insights, and training advice to help you jump higher, get stronger, and improve your basketball performance."
        canonical="https://bouncebunny.app/blog"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Blog',
          name: 'BounceBunny Blog',
          description: 'Tips, insights, and training advice to help you jump higher, get stronger, and improve your basketball performance.',
          url: 'https://bouncebunny.app/blog',
          publisher: {
            '@type': 'Organization',
            name: 'BounceBunny',
            logo: {
              '@type': 'ImageObject',
              url: 'https://bouncebunny.app/assets/logo.png',
            },
          },
          blogPost: posts.slice(0, 10).map((post) => ({
            '@type': 'BlogPosting',
            headline: post.title,
            url: `https://bouncebunny.app/blog/${post.slug}`,
            datePublished: post.publishedAt,
            image: post.coverImage || undefined,
            description: post.brief,
          })),
        }}
      />
      <div className="container mx-auto px-4 py-12 md:py-16">
        {/* Header */}
        <header className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            BounceBunny Blog
          </h1>
          <p className="text-lg text-white/70 max-w-2xl mx-auto">
            Tips, insights, and training advice to help you perform better
          </p>
        </header>

        {/* Tags */}
        {tags.length > 0 && (
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {tags.slice(0, 8).map((tag) => (
              <Link
                key={tag.slug}
                href={`/blog/tag/${tag.slug}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-sm transition-colors"
              >
                <Tag className="w-3.5 h-3.5" />
                {tag.name}
                <span className="text-white/50">({tag.count})</span>
              </Link>
            ))}
          </div>
        )}

        {/* Posts Grid */}
        {posts.length === 0 ? (
          <div className="text-center py-16">
            <BookOpen className="w-16 h-16 mx-auto mb-4 text-white/30" />
            <h2 className="text-xl font-semibold mb-2">No posts yet</h2>
            <p className="text-white/60">
              Check back soon for training tips and insights!
            </p>
          </div>
        ) : (
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {posts.map((post) => (
              <article
                key={post.id}
                className="group bg-white/5 rounded-xl overflow-hidden hover:bg-white/10 transition-colors"
              >
                <Link href={`/blog/${post.slug}`}>
                  {/* Cover Image */}
                  {post.coverImage ? (
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={post.coverImage}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    </div>
                  ) : (
                    <div className="aspect-video bg-gradient-to-br from-blue-600/20 to-purple-600/20 flex items-center justify-center">
                      <BookOpen className="w-12 h-12 text-white/30" />
                    </div>
                  )}

                  {/* Content */}
                  <div className="p-5">
                    {/* Tags */}
                    {post.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {post.tags.slice(0, 2).map((tag) => (
                          <span
                            key={tag.slug}
                            className="text-xs px-2 py-0.5 rounded bg-white/10 text-white/70"
                          >
                            {tag.name}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Title */}
                    <h2 className="text-lg font-semibold mb-2 group-hover:text-blue-400 transition-colors line-clamp-2">
                      {post.title}
                    </h2>

                    {/* Brief */}
                    <p className="text-sm text-white/60 mb-4 line-clamp-2">
                      {post.brief}
                    </p>

                    {/* Meta */}
                    <div className="flex items-center justify-between text-xs text-white/50">
                      <span>{formatDate(post.publishedAt)}</span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {post.readTimeInMinutes} min read
                      </span>
                    </div>
                  </div>
                </Link>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
